import * as React from 'react'
import { Text, View, ScrollView, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'
import Steps from '@components/steps/Steps'
import TextInput from '@components/textInput/TextInput'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import IncidentCard from '@components/incidentCard/IncidentCard'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import KeyboardView from '@components/view/KeyboardView'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import { allStepsEDL, findTenant, findManifestation } from '../utils'

import { EtatDesLieuxTypeEnum } from '@api/enum'

import useReducer from '@store/useReducer'
import * as EDLStore from '@store/edl/edl'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as IncidentStore from '@store/incident/incident'
import * as LocationStore from '@store/location/location'

import { findLocalisationSubLevels } from '@screens/incident/utils'

type Section = { title: string; data: IncidentResponse[] }

interface Props {
  id: string
}

const EDLRecapScreen = ({ id }: Props) => {
  const scroll = React.useRef<ScrollView>(null)

  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const form = useReducer(EDLStore.store, (s) => s.form)
  const forms = useReducer(EDLStore.store, (s) => s.forms)
  const user = useReducer(UserStore.store, (s) => s.user!)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)

  const places = useReducer(LocationStore.store, (s) => s.places)
  const spaces = React.useMemo(() => places.filter((p) => !p.parentId), [places])

  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const [open, setOpen] = React.useState(true)
  const [tab, setTab] = React.useState<'ORGA' | 'VIPARIS'>('ORGA')

  const manif = React.useMemo(() => findManifestation(manifestations, form.manifestationId), [manifestations, form])
  const tenant = React.useMemo(() => findTenant(tenants, form.locatairePermanentId), [tenants, form])

  const formIncidents = React.useMemo(
    () =>
      incidents
        .filter((i) => form.incidentAcceptIds.includes(i.id))
        .sort((a, b) => b.dateDeCreation.localeCompare(a.dateDeCreation)),
    [incidents, form]
  )

  const incidentsOrga = React.useMemo(
    () => formIncidents.filter((i) => form.incidentOrganisateurIds.includes(i.id)),
    [formIncidents, form]
  )

  const incidentsViparis = React.useMemo(
    () => formIncidents.filter((i) => form.incidentViparisIds.includes(i.id)),
    [formIncidents, form]
  )

  const formSpaces = React.useMemo(
    () => spaces.filter((p) => form.espaceIds.includes(p.id)).sort((a, b) => a.libelle.localeCompare(b.libelle)),
    [form, spaces]
  )

  const tabs: { label: string; value: 'ORGA' | 'VIPARIS' }[] = [
    { label: i18n.t('screens.edlRecap.incidentsOrganisatorTab', { number: incidentsOrga.length }), value: 'ORGA' },
    { label: i18n.t('screens.edlRecap.incidentsViparisTab', { number: incidentsViparis.length }), value: 'VIPARIS' },
  ]

  const steps: Step[] = allStepsEDL(2, i18n)

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t(`screens.edl.title`), onClick: () => navigation.push(`/edl`) },
  ]

  const sections: Section[] = React.useMemo(
    () =>
      formSpaces.map((s) => {
        const locations = [s.id, ...findLocalisationSubLevels(places, s).map((l) => l.id)]

        return {
          title: s.libelle,
          data: formIncidents.filter((inc) => inc.localisationId && locations.includes(inc.localisationId)),
        }
      }),
    [formSpaces, places, formIncidents]
  )

  React.useEffect(() => {
    if (!!form) {
      const updateForms = forms.map((f) => (f.id === form.id && f.type === form.type ? form : f))
      EDLStore.actions.setForms(updateForms)
    }
  }, [form])

  const renderItem = (item: IncidentResponse) => {
    return (
      <IncidentContainer
        key={item.id}
        activeOpacity={0.9}
        onPress={() => navigation.push(`/incidentDetail/${item.id}`)}>
        <IncidentCard
          incident={item}
          displayPrice={form.type === EtatDesLieuxTypeEnum.SORTIE && user.typeUtilisateur === 'VIPARIS'}
        />
      </IncidentContainer>
    )
  }

  const renderSpace = (section: Section) => {
    return (
      <PlaceContainer key={section.title}>
        <PlaceName>
          {i18n.t('screens.edlRecap.placeName', { name: section.title, number: section.data.length })}
        </PlaceName>

        <IncidentsList>{section.data.map(renderItem)}</IncidentsList>
      </PlaceContainer>
    )
  }

  const renderData = (sections: Section[]) => {
    // if exit edl, split between organizer and viparis
    if (form.type === EtatDesLieuxTypeEnum.SORTIE) {
      const orgaSections = sections
        .map((s) => ({ ...s, data: s.data.filter((i) => form.incidentOrganisateurIds.includes(i.id)) }))
        .filter((s) => s.data.length > 0)
      const orgaSize = orgaSections.reduce((acc, cur) => acc + cur.data.length, 0)

      const viparisSections = sections
        .map((s) => ({ ...s, data: s.data.filter((i) => form.incidentViparisIds.includes(i.id)) }))
        .filter((s) => s.data.length > 0)
      const viparisSize = viparisSections.reduce((acc, cur) => acc + cur.data.length, 0)

      if (!isPhone) {
        return (
          <>
            <SubSection>
              <SubSectionTitleContainer>
                <SectionTitle title={i18n.t('screens.edlRecap.incidentsOrganisator', { number: orgaSize })} />
              </SubSectionTitleContainer>
              {orgaSections.map(renderSpace)}
            </SubSection>

            <SubSection>
              <SubSectionTitleContainer>
                <SectionTitle title={i18n.t('screens.edlRecap.incidentsViparis', { number: viparisSize })} />
              </SubSectionTitleContainer>
              {viparisSections.map(renderSpace)}
            </SubSection>
          </>
        )
      }

      return (
        <>
          <TabContainer>
            <SegmentedControl
              data={tabs}
              value={tabs.find((t) => t.value === tab)}
              onItemSelected={(opt) => setTab(opt.value)}
            />
          </TabContainer>

          {tab === 'ORGA' ? orgaSections.map(renderSpace) : viparisSections.map(renderSpace)}
        </>
      )
    }

    return sections.map(renderSpace)
  }

  const renderIncidents = () => {
    const size = formIncidents.length

    const name = isPhone
      ? form.type === EtatDesLieuxTypeEnum.ENTREE
        ? 'incidentsTitlePhone'
        : 'newIncidentsPhone'
      : form.type === EtatDesLieuxTypeEnum.ENTREE
      ? 'incidentsTitle'
      : 'newIncidents'

    return (
      <>
        <SectionContainer activeOpacity={0.8} onPress={() => setOpen(!open)} disabled={!isPhone}>
          <SectionTitleContainer>
            <SectionTitle title={i18n.t(`screens.edlRecap.${name}`, { number: size })} />
          </SectionTitleContainer>

          {isPhone && (
            <>
              <TagIncident>
                <NumberIncident>{size}</NumberIncident>
              </TagIncident>
              <ArrowContainer>
                <Icon name={open ? 'arrow-up' : 'arrow-down'} color={theme.colors.primary} />
              </ArrowContainer>
            </>
          )}
        </SectionContainer>

        {(!isPhone || open) && renderData(sections)}
      </>
    )
  }

  const renderBottomButton = () => {
    return (
      <BottomButton>
        <ButtonContainer>
          <Button textOnly label={i18n.t('screens.edl.spaces.back')} onClick={navigation.goBack} status="active" />
        </ButtonContainer>
        {!!form && (
          <ButtonContainer>
            <Button
              color="default"
              label={i18n.t('screens.edl.spaces.next')}
              onClick={() => navigation.push(`/edl/signature/${form.id}`)}
              status="active"
            />
          </ButtonContainer>
        )}
      </BottomButton>
    )
  }

  const renderComment = () => (
    <CommentContainer>
      <SectionTitle title={i18n.t('screens.edlRecap.comment')} />
      <InputContainer>
        <TextInput
          value={form.commentaire}
          onChange={(newComment) => EDLStore.actions.setForm({ commentaire: newComment })}
          placeholder={i18n.t('screens.edlRecap.addComment')}
          numberOfLines={8}
        />
      </InputContainer>
    </CommentContainer>
  )

  const renderHeader = () => {
    const name = !!manif ? manif.libelle : !!tenant ? tenant.tenant.nomLocatairePermanent : ''
    const date = !!form && !!form.dateDeCreation ? new Date(form.dateDeCreation) : new Date()

    return (
      <HeaderContainer>
        <InformationsContainer>
          <Row column={bp !== 'big'}>
            <InfosBold>
              {i18n.t(`screens.edlRecap.recapInProgressTitle`, { type: i18n.t(`screens.edlRecap.${form.type}`) })}
            </InfosBold>
            <CreationDate color={theme.colors.secondaryText}>
              {i18n.t('screens.edlRecap.creationDate', { date })}
            </CreationDate>
          </Row>

          <ManifestationName>{name}</ManifestationName>

          {bp === 'phone' && <Separator />}

          <SpaceName>{formSpaces.map((s) => s.libelle).join(', ')}</SpaceName>
        </InformationsContainer>

        <ContactContainer>
          <SectionTitle title={i18n.t('screens.edlRecap.contact')} />

          <ContactInfosContainer paddingTop={bp === 'phone' ? 0 : 32}>
            <CreationDate color={theme.colors.primaryText} bold body={bp === 'phone'}>
              {`${user.prenom} ${user.nom}`}
            </CreationDate>
            {bp !== 'phone' ? (
              <CreationDate color={theme.colors.secondaryText}>
                {i18n.t('screens.edlRecap.contactRole', { role: user.fonction })}
              </CreationDate>
            ) : (
              <Icon name="email" size={20} color={theme.colors.primary} />
            )}
          </ContactInfosContainer>

          {bp === 'phone' && <InfosBold grey>{user.fonction}</InfosBold>}

          {bp !== 'phone' && (
            <ContactInfosContainer paddingTop={16}>
              <IconContainer>
                <Icon name="email" size={20} color={theme.colors.primary} />
              </IconContainer>

              <CreationDate color={theme.colors.primaryText}>{user.email}</CreationDate>
            </ContactInfosContainer>
          )}
        </ContactContainer>
      </HeaderContainer>
    )
  }

  return (
    <Root
      onLeft={navigation.goBack}
      header={i18n.t('screens.edl.title')}
      renderSubHeader={() => <Steps steps={steps} />}
      renderFixedBottom={renderBottomButton}
      fixed>
      <ScrollContainer
        scrollIndicatorInsets={{ right: 1 }}
        bounces={false}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        ref={scroll}>
        {!isPhone && <Header title={i18n.t('screens.edlRecap.stepTitle')} links={headerLinks} />}
        <MainHeaderContainer>{renderHeader()}</MainHeaderContainer>

        {renderIncidents()}

        <KeyboardView>{renderComment()}</KeyboardView>
      </ScrollContainer>
    </Root>
  )
}

export default EDLRecapScreen

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
`

const MainHeaderContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 24px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 0px 16px 16px;'};
  background-color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.background : props.theme.colors.primary};
`

const HeaderContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && 'margin-top: 16px;'};
  margin-bottom: ${(props) => (props.theme.breakpoint !== 'phone' ? '24' : '16')}px;
  background-color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.incident : props.theme.colors.background};
  border-radius: 8px;
  flex-direction: ${(props) => (props.theme.breakpoint !== 'big' ? 'column' : 'row')};
`

const InformationsContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'big' && 'flex: 1;'};
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 24px 32px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 16px;'};
`

const ContactContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 360px;'};
  background-color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.background : props.theme.colors.incident};
  border-radius: 8px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin: 8px 34px 34px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'margin: 8px 16px 16px;'};
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 32px 24px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 16px 16px 70px;'};
`

const InfosBold = styled(Text)<{ grey?: boolean }>`
  ${(props) => (props.theme.breakpoint !== 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Body)};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding-bottom: 8px'};
  ${(props) => props.grey && `color: ${props.theme.colors.secondaryText};`}
`

const ManifestationName = styled(Text)`
  ${(props) => (props.theme.breakpoint !== 'phone' ? props.theme.fonts.Heading1 : props.theme.fonts.Title3)};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 16px 0px;'};
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 16px 0px 8px;'};
  ${(props) => props.theme.breakpoint === 'phone' && '  text-align: center;'};
`

const SpaceName = styled(Text)`
  ${(props) => (props.theme.breakpoint !== 'phone' ? props.theme.fonts.Body : props.theme.fonts.Caption1)};
  color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.secondaryText : props.theme.colors.primaryText};
`

const CreationDate = styled(Text)<{ color: string; bold?: boolean; body?: boolean }>`
  ${(props) =>
    props.body
      ? props.theme.fonts.Body
      : props.theme.breakpoint === 'phone'
      ? props.theme.fonts.Caption1
      : props.bold
      ? props.theme.fonts.Callout
      : props.theme.fonts.Body};
  color: ${(props) => props.color};
`

const Row = styled(View)<{ column?: boolean }>`
  flex-direction: ${(props) => (props.column ? 'column' : 'row')};
  ${(props) => (!props.column || props.theme.breakpoint === 'phone') && 'align-items: center;'};
  ${(props) => !props.column && 'justify-content: space-between;'};
`

const ContactInfosContainer = styled(View)<{ paddingTop: number }>`
  flex-direction: row;
  padding-top: ${(props) => props.paddingTop}px;
  align-items: center;
  ${(props) => props.theme.breakpoint === 'phone' && 'justify-content: space-between;'};
`

const IconContainer = styled(View)`
  padding-right: 10px;
`

const Separator = styled(View)`
  height: 2px;
  background-color: ${(props) => props.theme.colors.disable};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1;'};
  ${(props) => props.theme.breakpoint !== 'phone' && '  margin-left: 16px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'width: 100%;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'margin-bottom: 16px;'};
`

const BottomButton = styled(View)`
  justify-content: ${(props) => (props.theme.breakpoint === 'phone' ? 'space-between' : 'center')};
  align-items: center;
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  border: 0 solid ${(props) => props.theme.colors.border};
  ${(props) => props.theme.breakpoint !== 'phone' && 'border-top-width: 1px;'};
  width: ${(props) => (props.theme.breakpoint === 'medium' || props.theme.breakpoint === 'phone' ? '200' : '300')}px;
  padding: 16px 48px;
`

const CommentContainer = styled(View)`
  margin: 24px;
`

const InputContainer = styled(View)`
  margin-top: 24px;
`
const PlaceContainer = styled(View)`
  padding-top: 16px;
`

const IncidentContainer = styled(TouchableOpacity)`
  padding: 16px;
`

const PlaceName = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin: 0 24px;
`

const NumberIncident = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const IncidentsList = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
  padding: 0 8px;
`

const SubSection = styled(View)`
  padding-top: 16px;
`

const SectionContainer = styled(TouchableOpacity)`
  margin: 0 24px;
  margin-top: 5px;
  flex-direction: row;
  align-items: center;
`

const SectionTitleContainer = styled(View)`
  flex: 1;
`

const TagIncident = styled(View)`
  background-color: ${(props) => props.theme.colors.secondaryActive};
  border-radius: 30px;
  height: 32px;
  width: 32px;
  align-items: center;
  justify-content: center;
`

const ArrowContainer = styled(View)`
  padding: 16px;
`

const SubSectionTitleContainer = styled(View)`
  margin: 8px 24px;
  align-items: flex-start;
`

const TabContainer = styled(View)`
  margin: 8px 24px;
`
